import React, { useEffect, useRef } from "react";
import headerImage from "./assets/vitrine/guide/header.png";
import guideImage from "./assets/vitrine/guide/guide1.png";
import guideVideo from "./assets/vitrine/guide/guide.mp4";
import "./GuidePage.css";

const GOOGLE_PLAY_URL = "https://play.google.com/store/apps/details?id=com.akremanaya.weavevomobile&hl=fr";

const PreviewVideo = ({ src }) => {
  const ref = useRef(null);

  const playWithSound = () => {
    const video = ref.current;
    if (!video) return;
    video.muted = false;
    const promise = video.play();
    if (promise?.catch) promise.catch(() => {});
  };

  const keepPlayingMuted = () => {
    const video = ref.current;
    if (!video) return;
    video.muted = true;
    const promise = video.play();
    if (promise?.catch) promise.catch(() => {});
  };

  return (
    <div className="guide-videoFrame" onMouseEnter={playWithSound} onMouseLeave={keepPlayingMuted}>
      <video ref={ref} controls autoPlay muted loop playsInline preload="auto">
        <source src={src} type="video/mp4" />
      </video>
    </div>
  );
};

export default function GuidePage() {
  useEffect(() => {
    document.title = "City & Local Guides | Weavevo";
    window.scrollTo(0, 0);
  }, []);

  return (
    <main className="guide-page">
      <section className="guide-hero">
        <img src={headerImage} alt="" />
        <div className="guide-heroScrim" />
        <div className="guide-heroCopy">
          <h1>City & Local Guides</h1>
          <p>Transform static city tours into collaborative, perpetually updated Living Weaves.</p>
        </div>
      </section>

      <section className="guide-section guide-videoSection">
        <PreviewVideo src={guideVideo} />
      </section>

      <section className="guide-section guide-textGrid">
        <article className="guide-card">
          <h2>1. The Living Walking Tour (Zero Obsolescence)</h2>
          <h3>Never lose a view or reprint a QR code when a city landmark changes.</h3>
          <p>Traditional video guides age quickly: venues close, facades go under renovation, and schedules change. With Weavevo’s modular architecture, you don’t discard your video. Use the AI Scene Wizard to hot-swap a single 30-second sequence from your phone. The Loom re-stitches the updated path instantly—keeping your original link, view count, and distributed QR codes intact.</p>
          <ul>
            <li><strong>Hot-Swap Engine:</strong> Replace outdated stops without breaking syndication links.</li>
            <li><strong>Preserved Metrics:</strong> Retain 100% of your accumulated audience and SEO authority.</li>
            <li><strong>Living Map Sync:</strong> Automatically plot every update on interactive visitor maps.</li>
          </ul>
        </article>

        <article className="guide-card">
          <h2>2. Frictionless Co-Creation with Local Experts</h2>
          <h3>Unite historians, artisans, and chefs into a single choral narrative.</h3>
          <p>You don’t need an expensive film crew or heavy desktop editing software to build a rich multi-perspective tour. Direct the flow, keep the core historical narrative, and delegate specific stops to neighborhood specialists:</p>
          <ul>
            <li>The baker reveals the secret behind morning pastries.</li>
            <li>The leather artisan opens their workshop doors.</li>
            <li>The local historian unveils forgotten street legends.</li>
          </ul>
          <p>Each contributor records their piece on mobile using your shared Blueprint. The platform unifies the rush footage into a seamless, multi-voice city journey.</p>
        </article>
      </section>

      <section className="guide-section guide-imageSection">
        <img src={guideImage} alt="" loading="lazy" />
      </section>

      <section className="guide-section guide-textGrid">
        <article className="guide-card">
          <h2>3. Dynamic Seasonal Agility</h2>
          <h3>Adapt your route to the rhythm of the city.</h3>
          <p>Cities change with the seasons, and your guides should too. Swap winter holiday markets for summer open-air terraces in minutes. Weave temporary cultural festivals, night markets, and pop-up exhibitions into your evergreen routes, ensuring travelers always experience the city in its present state.</p>
        </article>

        <article className="guide-card">
          <h2>4. Scalable Monetization & Direct Attribution</h2>
          <h3>Turn municipal marketing and hotel partnerships into continuous revenue.</h3>
          <p>Distribute your Weaves where tourists actually are:</p>
          <ul>
            <li><strong>Hotel & Concierge Portals:</strong> Place verified QR codes in hotel lobbies and tourist desks.</li>
            <li><strong>Fair Automated Splits:</strong> Every time a living guide is accessed, sponsored, or tipped, the In-App Wallet & Escrow automatically distributes earnings between the guide, local creators, and retail partners.</li>
            <li><strong>100% Contributor Credit:</strong> Full attribution is baked into every capsule, boosting your local authority and brand reputation.</li>
          </ul>
        </article>
      </section>

      <section className="guide-final">
        <h2>Ready to build your first Living Tour?</h2>
        <p>Start weaving collaborative routes or onboard your local network today.</p>
        <div className="guide-storeActions">
          <a href={GOOGLE_PLAY_URL} target="_blank" rel="noreferrer" className="guide-storeButton">
            <i className="bi bi-google-play" aria-hidden="true" />
            <span>Play Store</span>
          </a>
          <button type="button" className="guide-storeButton" disabled>
            <i className="bi bi-apple" aria-hidden="true" />
            <span>App Store</span>
          </button>
        </div>
      </section>
    </main>
  );
}
